import React from "react";
import RegisterPage, { STATUS_BADGES, StatCard } from "../components/RegisterPage";
import { useHSE } from "../lib/store";
import { apiFetch } from "../lib/api";

export default function IndustrialHygiene() {
  const { data, siteNames } = useHSE();
  const rows = data.industrialHygieneEvents || data.industrialHygiene || [];

  const handleSaveRecord = async (editingRecord) => {
    try {
      const res = await apiFetch("/industrial-hygiene", {
        method: "POST",
        body: {
          ...editingRecord,
          idempotencyKey: `ih-${editingRecord.id}`,
        },
      });
      return res?.event ? { ...editingRecord, ...res.event } : editingRecord;
    } catch (err) {
      return editingRecord;
    }
  };

  const Top = (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <StatCard label="Total Sampling Events" value={rows.length} testid="ih-total" />
      <StatCard label="Acceptable Results" value={rows.filter((r) => r.resultClassification === "acceptable" || !r.isExceedance).length} tone="safe" />
      <StatCard label="Action Level" value={rows.filter((r) => r.resultClassification === "action_level").length} tone="warning" />
      <StatCard label="OEL Exceedances" value={rows.filter((r) => r.isExceedance || r.resultClassification === "exceedance").length} tone="critical" />
    </div>
  );

  return (
    <RegisterPage
      title="Industrial Hygiene & Exposure Monitoring"
      subtitle="Similar Exposure Groups (SEGs), Hazard Monitoring, OEL Compliance & Medical Surveillance Tracking"
      storeKey="industrialHygieneEvents"
      idPrefix="IH"
      topPanels={Top}
      onSaveRecord={handleSaveRecord}
      columns={[
        { key: "id", label: "Sample ID" },
        { key: "agentName", label: "Agent / Hazard" },
        { key: "sampleDate", label: "Date" },
        { key: "measurementValue", label: "Measured Value", render: (r) => <span className="font-mono font-bold">{r.measurementValue} {r.measurementUnit}</span> },
        { key: "oelLimit", label: "OEL Limit", render: (r) => <span className="font-mono text-slate-500">{r.oelLimit} {r.measurementUnit}</span> },
        { key: "resultClassification", label: "Result", render: (r) => <span className={`uppercase text-[10px] font-bold px-2 py-0.5 rounded ${r.isExceedance || r.resultClassification === "exceedance" ? "bg-rose-100 text-rose-800" : r.resultClassification === "action_level" ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}>{r.resultClassification || (r.isExceedance ? "exceedance" : "acceptable")}</span> },
        { key: "medicalReferralRequired", label: "Medical Referral", render: (r) => r.medicalReferralRequired ? <span className="text-rose-600 font-bold">Required</span> : <span className="text-slate-400">None</span> },
        { key: "actionId", label: "Linked CAPA", render: (r) => r.actionId ? <span className="font-mono text-[11px] text-[#18B84F]">{r.actionId}</span> : <span className="text-slate-300">—</span> },
      ]}
      badgeMap={{ status: STATUS_BADGES }}
      formFields={[
        { key: "id", label: "Sample ID" },
        { key: "site", label: "Site", type: "select", options: siteNames },
        { key: "segId", label: "SEG ID / Code" },
        { key: "agentName", label: "Hazard Agent Name", type: "select", options: ["Silica Dust", "Noise (8h TWA)", "Benzene Vapor", "Lead Dust", "Heat Stress WBGT", "Radiation"] },
        { key: "sampleDate", label: "Sample Date", type: "date" },
        { key: "measurementValue", label: "Measured Value", type: "number" },
        { key: "measurementUnit", label: "Unit", type: "select", options: ["mg/m3", "dBA", "ppm", "W/m2"] },
        { key: "oelLimit", label: "OEL Regulatory Limit", type: "number" },
        { key: "samplingMethod", label: "Sampling Method Reference" },
        { key: "labReportRef", label: "Lab Report External Ref" },
        { key: "medicalReferralRequired", label: "Medical Referral Required?", type: "select", options: [false, true] },
      ]}
    />
  );
}
